// Hook personalizado para permisos
import { useAuth } from '@/contexts/AuthContext';
import { useSimulation } from '@/contexts/SimulationContext';

/**
 * Mapeo de módulos legacy a módulos de la matriz de permisos
 * El sistema legacy usa: module.action (ej: user_management.read)
 * La matriz usa: module.action (ej: users.view)
 */
const LEGACY_TO_MATRIX_MAP: Record<string, { module: string; actions: Record<string, string> }> = {
  'dashboard': { module: 'dashboard', actions: { 'read': 'view', 'write': 'edit' } },
  'user_management': { module: 'users', actions: { 'read': 'view', 'write': 'edit', 'delete': 'delete' } },
  'controllers': { module: 'controllers', actions: { 'read': 'view', 'write': 'edit', 'delete': 'delete' } },
  'access_control': { module: 'access_points', actions: { 'read': 'view', 'write': 'edit', 'delete': 'delete' } },
  'audit': { module: 'logs', actions: { 'read': 'view' } },
  'settings': { module: 'settings', actions: { 'read': 'view', 'write': 'edit' } },
  'bookings': { module: 'bookings', actions: { 'view': 'view', 'manage': 'manage', 'create': 'create' } },
  'roles': { module: 'roles', actions: { 'read': 'view', 'write': 'edit', 'delete': 'delete' } },
};

/**
 * Mapeo inverso: módulos matrix → módulos legacy
 * Permite usar claves matrix (ej: roles.view) que se resuelven contra permisos legacy
 */
const MATRIX_TO_LEGACY_MAP: Record<string, { module: string; actions: Record<string, string> }> = {
  // Core modules → legacy equivalents
  'users': { module: 'user_management', actions: { 'view': 'read', 'create': 'write', 'edit': 'write', 'delete': 'delete', 'manage_permissions': 'write' } },
  'roles': { module: 'user_management', actions: { 'view': 'read', 'create': 'write', 'edit': 'write', 'delete': 'delete' } },
  'access_points': { module: 'access_control', actions: { 'view': 'read', 'create': 'write', 'edit': 'write', 'delete': 'delete', 'open': 'write', 'lock': 'write' } },
  'logs': { module: 'audit', actions: { 'view': 'read', 'export': 'read', 'delete': 'delete' } },
  'audit': { module: 'audit', actions: { 'view': 'read' } },
  'dashboard': { module: 'dashboard', actions: { 'view': 'read', 'edit': 'write', 'business': 'read', 'operational': 'read', 'export': 'read' } },
  'controllers': { module: 'controllers', actions: { 'view': 'read', 'create': 'write', 'edit': 'write', 'delete': 'delete', 'sync': 'write', 'reboot': 'write', 'factory_reset': 'write', 'firmware': 'write' } },
  'settings': { module: 'settings', actions: { 'view': 'read', 'edit': 'write' } },
  // Reportes → legacy reports module
  'reports': { module: 'reports', actions: { 'view': 'read', 'security': 'read', 'hr': 'read', 'operations': 'read', 'export': 'write' } },
  // Corporate sub-modules → legacy "corporate" catch-all
  'departments': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'edit': 'update', 'delete': 'delete' } },
  'employees': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'edit': 'update', 'delete': 'delete', 'bulk_import': 'create' } },
  'visitors': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'edit': 'update', 'approve': 'update', 'cancel': 'update', 'delete': 'delete', 'checkin': 'view' } },
  'sedes': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'edit': 'update', 'delete': 'delete' } },
  'credentials': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'edit': 'update', 'revoke': 'delete' } },
  'access_groups': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'edit': 'update', 'delete': 'delete' } },
  'spaces': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'edit': 'update', 'delete': 'delete' } },
  'bookings': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'manage': 'update' } },
  'parking': { module: 'corporate', actions: { 'view': 'view', 'create': 'create', 'edit': 'update', 'delete': 'delete', 'configure': 'update' } },
};

/**
 * Mapa de expansión: cada clave legacy (module.action) se expande
 * a una lista de claves matrix equivalentes.
 * Se usa para convertir permisos legacy de la BD al formato matrix de la UI.
 */
const LEGACY_EXPANSION_MAP: Record<string, string[]> = {
  // Dashboard
  'dashboard.read': ['dashboard.view', 'dashboard.business', 'dashboard.operational', 'dashboard.export'],
  'dashboard.write': ['dashboard.edit'],
  // User Management → Users + Roles
  'user_management.read': ['users.view', 'roles.view'],
  'user_management.write': ['users.create', 'users.edit', 'users.manage_permissions', 'roles.create', 'roles.edit'],
  'user_management.delete': ['users.delete', 'roles.delete'],
  // Controllers
  'controllers.read': ['controllers.view'],
  'controllers.write': ['controllers.create', 'controllers.edit', 'controllers.sync', 'controllers.reboot', 'controllers.factory_reset', 'controllers.firmware'],
  'controllers.delete': ['controllers.delete'],
  // Access Control → Access Points
  'access_control.read': ['access_points.view'],
  'access_control.write': ['access_points.create', 'access_points.edit', 'access_points.open', 'access_points.lock'],
  'access_control.delete': ['access_points.delete'],
  // Audit → Logs + Audit
  'audit.read': ['logs.view', 'logs.export', 'audit.view'],
  'audit.write': ['logs.delete'],
  // Settings
  'settings.read': ['settings.view'],
  'settings.write': ['settings.edit'],
  // Reports — legacy read expande a todas las categorías de vista
  'reports.read': ['reports.view', 'reports.security', 'reports.hr', 'reports.operations'],
  'reports.write': ['reports.export'],
  // Corporate catch-all → todos los sub-módulos corporativos
  'corporate.view': ['departments.view', 'employees.view', 'visitors.view', 'sedes.view', 'credentials.view', 'access_groups.view', 'spaces.view', 'bookings.view', 'parking.view'],
  'corporate.create': ['departments.create', 'employees.create', 'employees.bulk_import', 'visitors.create', 'sedes.create', 'credentials.create', 'access_groups.create', 'spaces.create', 'bookings.create', 'parking.create'],
  'corporate.update': ['departments.edit', 'employees.edit', 'visitors.edit', 'visitors.approve', 'visitors.cancel', 'visitors.checkin', 'sedes.edit', 'credentials.edit', 'access_groups.edit', 'spaces.edit', 'bookings.manage', 'parking.edit', 'parking.configure'],
  'corporate.delete': ['departments.delete', 'employees.delete', 'visitors.delete', 'sedes.delete', 'credentials.revoke', 'access_groups.delete', 'spaces.delete', 'parking.delete'],
  // System (si existiera en legacy)
  'system.admin': ['system.admin', 'system.backup'],
};

/**
 * Normaliza permisos legacy al formato matrix.
 * Expande las claves legacy (user_management.read, corporate.view)
 * a sus equivalentes matrix (users.view, departments.view) para que
 * la UI de la matriz los muestre correctamente.
 *
 * Preserva cualquier clave matrix que ya exista en el objeto original.
 */
export function normalizePermissionsToMatrix(
  legacyPermissions: Record<string, Record<string, boolean>>
): Record<string, Record<string, boolean>> {
  const result: Record<string, Record<string, boolean>> = {};

  // 1. Copiar todas las claves existentes (incluye matrix ya presentes)
  for (const [mod, actions] of Object.entries(legacyPermissions)) {
    if (!result[mod]) result[mod] = {};
    for (const [action, value] of Object.entries(actions)) {
      result[mod][action] = value;
    }
  }

  // 2. Expandir claves legacy a matrix
  for (const [mod, actions] of Object.entries(legacyPermissions)) {
    for (const [action, value] of Object.entries(actions)) {
      if (value !== true) continue;

      const legacyKey = `${mod}.${action}`;
      const expansions = LEGACY_EXPANSION_MAP[legacyKey];
      if (expansions) {
        for (const matrixKey of expansions) {
          const [matrixMod, matrixAction] = matrixKey.split('.');
          if (!result[matrixMod]) result[matrixMod] = {};
          result[matrixMod][matrixAction] = true;
        }
      }
    }
  }

  return result;
}

export const usePermissions = () => {
  const { permissions: realPermissions, hasAnyPermission, hasAllPermissions } = useAuth();
  const { isSimulating, simulatedPermissions } = useSimulation();

  // Usar permisos simulados si está en modo simulación
  const permissions = isSimulating && simulatedPermissions ? simulatedPermissions : realPermissions;

  /**
   * Función de verificación bidireccional que considera la simulación.
   * Acepta tanto claves legacy (user_management.read) como matrix (roles.view)
   * y busca en ambas direcciones contra el objeto de permisos actual.
   */
  const hasPermission = (module: string, action: string): boolean => {
    if (!permissions) return false;

    // 1. Verificación directa (funciona si la clave coincide con el formato actual)
    if (permissions[module]?.[action] === true) {
      return true;
    }

    // 2. Legacy → Matrix: si la clave es legacy, buscar en formato matrix
    const legacyMapping = LEGACY_TO_MATRIX_MAP[module];
    if (legacyMapping) {
      const matrixModule = legacyMapping.module;
      const matrixAction = legacyMapping.actions[action] || action;
      if (permissions[matrixModule]?.[matrixAction] === true) {
        return true;
      }
    }

    // 3. Matrix → Legacy: si la clave es matrix, buscar en formato legacy
    const matrixMapping = MATRIX_TO_LEGACY_MAP[module];
    if (matrixMapping) {
      const legacyModule = matrixMapping.module;
      const legacyAction = matrixMapping.actions[action] || action;
      if (permissions[legacyModule]?.[legacyAction] === true) {
        return true;
      }
    }

    return false;
  };

  /**
   * Verificar permisos comunes
   */
  const can = {
    // Función genérica para verificar cualquier permiso (útil para la matriz)
    hasPermission,

    // Users
    viewUsers: () => hasPermission('users', 'view'),
    createUsers: () => hasPermission('users', 'create'),
    updateUsers: () => hasPermission('users', 'edit'),
    deleteUsers: () => hasPermission('users', 'delete'),

    // Roles
    viewRoles: () => hasPermission('roles', 'view'),
    createRoles: () => hasPermission('roles', 'create'),
    updateRoles: () => hasPermission('roles', 'edit'),
    deleteRoles: () => hasPermission('roles', 'delete'),

    // Controllers
    viewControllers: () => hasPermission('controllers', 'view'),
    updateControllers: () => hasPermission('controllers', 'edit'),
    deleteControllers: () => hasPermission('controllers', 'delete'),

    // Access Points
    viewAccessPoints: () => hasPermission('access_points', 'view'),
    updateAccessPoints: () => hasPermission('access_points', 'edit'),
    deleteAccessPoints: () => hasPermission('access_points', 'delete'),

    // Logs
    viewLogs: () => hasPermission('logs', 'view'),
    viewAudit: () => hasPermission('audit', 'view'),

    // Settings
    updateSettings: () => hasPermission('settings', 'edit'),

    // Bookings/Spaces (unificados)
    viewBookings: () => hasPermission('spaces', 'view') || hasPermission('bookings', 'view'),
    createBookings: () => hasPermission('spaces', 'create') || hasPermission('bookings', 'create'),
    manageBookings: () => hasPermission('spaces', 'edit') || hasPermission('bookings', 'manage'),
  };

  // Pre-calculate boolean permissions for easier use in components
  // Usando claves matrix para que funcionen en simulación Y en modo real (bidireccional)
  const viewDashboard = hasPermission('dashboard', 'view');
  const viewUsers = hasPermission('users', 'view');
  const viewRoles = hasPermission('roles', 'view');
  const viewControllers = hasPermission('controllers', 'view');
  const viewAccessPoints = hasPermission('access_points', 'view');
  const viewAccessLogs = hasPermission('logs', 'view');
  const viewAuditLogs = hasPermission('audit', 'view');

  // Dashboard granulares
  const viewDashboardBusiness = hasPermission('dashboard', 'business');
  const viewDashboardOperational = hasPermission('dashboard', 'operational');

  // Reportes granulares por categoría
  const viewReportsSecurity = hasPermission('reports', 'view') || hasPermission('reports', 'security');
  const viewReportsHr = hasPermission('reports', 'view') || hasPermission('reports', 'hr');
  const viewReportsOperations = hasPermission('reports', 'view') || hasPermission('reports', 'operations');
  const exportReports = hasPermission('reports', 'export');

  // Bookings/Spaces permissions (unificados - spaces.* cubre ambos)
  const viewBookings   = hasPermission('spaces', 'view')   || hasPermission('bookings', 'view');
  const createBookings = hasPermission('spaces', 'create') || hasPermission('bookings', 'create');
  const manageBookings = hasPermission('spaces', 'edit')   || hasPermission('bookings', 'manage');

  // Granular action permissions
  const createUsers = hasPermission('users', 'create');
  const updateUsers = hasPermission('users', 'edit');
  const deleteUsers = hasPermission('users', 'delete');

  const editControllers = hasPermission('controllers', 'edit');
  const deleteControllers = hasPermission('controllers', 'delete');
  const syncControllers = hasPermission('controllers', 'sync');
  const rebootControllers = hasPermission('controllers', 'reboot');

  const editAccessPoints = hasPermission('access_points', 'edit');
  const deleteAccessPoints = hasPermission('access_points', 'delete');
  const openAccessPoints = hasPermission('access_points', 'open');
  const lockAccessPoints = hasPermission('access_points', 'lock');

  const exportLogs = hasPermission('logs', 'export');

  // Roles permissions
  const createRoles = hasPermission('roles', 'create');
  const editRoles = hasPermission('roles', 'edit');
  const deleteRoles = hasPermission('roles', 'delete');

  // Sedes permissions
  const viewSedes = hasPermission('sedes', 'view');
  const createSedes = hasPermission('sedes', 'create');
  const editSedes = hasPermission('sedes', 'edit');
  const deleteSedes = hasPermission('sedes', 'delete');

  // Permissions management
  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    // Direct boolean permissions for convenience
    viewDashboard,
    viewUsers,
    viewRoles,
    viewControllers,
    viewAccessPoints,
    viewAccessLogs,
    viewAuditLogs,
    // Dashboard granulares
    viewDashboardBusiness,
    viewDashboardOperational,
    // Reportes granulares por categoría
    viewReportsSecurity,
    viewReportsHr,
    viewReportsOperations,
    exportReports,
    // Granular action permissions
    createUsers,
    updateUsers,
    deleteUsers,
    editControllers,
    deleteControllers,
    syncControllers,
    rebootControllers,
    editAccessPoints,
    deleteAccessPoints,
    openAccessPoints,
    lockAccessPoints,
    exportLogs,
    createRoles,
    editRoles,
    deleteRoles,
    // Bookings permissions
    viewBookings,
    manageBookings,
    createBookings,
    // Sedes permissions
    viewSedes,
    createSedes,
    editSedes,
    deleteSedes,
  };
};

export default usePermissions;
